import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import {
    HelperText,
    IconButton,
    TextInput as PaperTextInput,
    type TextInputProps as PaperTextInputProps,
    useTheme,
} from 'react-native-paper'
import type { AppTheme } from '@/types/theme'

export type NumberInputProps<T extends FieldValues = Record<string, unknown>> = Omit<
    PaperTextInputProps,
    'name' | 'value' | 'onChangeText' | 'onBlur' | 'keyboardType'
> & {
    name: Path<T>
    control: Control<T>
    label?: string
    hint?: string
    increment?: number
    min?: number
    max?: number
    wrapperProps?: ViewProps
}

export function NumberInput<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    label,
    hint,
    increment = 1,
    min,
    max,
    wrapperProps = {},
    ...inputProps
}: NumberInputProps<T>) {
    const theme = useTheme<AppTheme>()
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    const handleTextChange = (text: string) => {
        if (text === '' || text === '-') {
            field.onChange(text)
            return
        }

        const digitsOnly = text.replace(/[^0-9-]/g, '')
        if (digitsOnly !== text) {
            return
        }

        const numValue = Number.parseInt(digitsOnly, 10)
        if (!Number.isNaN(numValue)) {
            if (min !== undefined && numValue < min) {
                return
            }
            if (max !== undefined && numValue > max) {
                return
            }
            field.onChange(numValue)
        }
    }

    const handleIncrement = () => {
        const currentValue = typeof field.value === 'number' ? field.value : 0
        const newValue = currentValue + increment
        if (max === undefined || newValue <= max) {
            field.onChange(newValue)
        }
    }

    const handleDecrement = () => {
        const currentValue = typeof field.value === 'number' ? field.value : 0
        const newValue = currentValue - increment
        if (min === undefined || newValue >= min) {
            field.onChange(newValue)
        }
    }

    const displayValue =
        field.value === '' || field.value === '-' ? field.value : String(field.value ?? '')

    return (
        <View style={[styles.inputWrapper, wrapperProps.style]} {...wrapperProps}>
            <View style={styles.inputRow}>
                <IconButton
                    icon="minus"
                    mode="outlined"
                    onPress={handleDecrement}
                    disabled={inputProps.disabled || (min !== undefined && field.value <= min)}
                />
                <PaperTextInput
                    {...inputProps}
                    label={label}
                    mode="outlined"
                    value={displayValue}
                    onChangeText={handleTextChange}
                    onBlur={field.onBlur}
                    error={!!error}
                    keyboardType="numeric"
                    accessibilityLabel={label}
                    testID={name}
                    style={styles.numberInput}
                    contentStyle={styles.numberInputContent}
                    placeholderTextColor={theme.custom.placeholder}
                />
                <IconButton
                    icon="plus"
                    mode="outlined"
                    onPress={handleIncrement}
                    disabled={inputProps.disabled || (max !== undefined && field.value >= max)}
                />
            </View>
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    numberInput: {
        flex: 1,
        minWidth: 60,
    },
    numberInputContent: {
        textAlign: 'center',
    },
})
